// client/src/pages/ShopPOS/components/OfflineIndicator.tsx
import { Button } from "@/components/ui/button";
import { WifiOff, RefreshCw } from "lucide-react";

interface OfflineIndicatorProps {
  isOfflineMode: boolean;
  pendingSalesCount: number;
  isSyncing: boolean;
  syncProgress: { current: number; total: number; isSyncing: boolean };
  onSync: () => void;
}

export function OfflineIndicator({
  isOfflineMode,
  pendingSalesCount,
  isSyncing,
  syncProgress,
  onSync,
}: OfflineIndicatorProps) {
  if (!isOfflineMode && pendingSalesCount === 0) {
    return null;
  }

  return (
    <>
      {/* Versión Desktop (a la derecha) */}
      <div className="hidden md:block absolute top-16 right-4 w-96 z-50 space-y-2">
        {isOfflineMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-lg p-3 flex items-center gap-2">
            <WifiOff className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                Modo sin conexión
              </p>
              <p className="text-xs text-amber-700">
                Las ventas se guardarán localmente
              </p>
            </div>
          </div>
        )}

        {pendingSalesCount > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw
                className={`h-5 w-5 text-blue-600 flex-shrink-0 ${isSyncing ? "animate-spin" : ""}`}
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">
                  {syncProgress.isSyncing && !isOfflineMode
                    ? `Sincronizando ${syncProgress.current}/${syncProgress.total}...`
                    : `${pendingSalesCount} operacione${pendingSalesCount !== 1 ? "s" : ""} pendiente${pendingSalesCount !== 1 ? "s" : ""}`}
                </p>
                <p className="text-xs text-blue-700">
                  {isOfflineMode
                    ? "Se sincronizarán cuando vuelva la conexión"
                    : isSyncing
                      ? "Procesando..."
                      : "Listas para sincronizar"}
                </p>
              </div>
            </div>
            {syncProgress.isSyncing && !isOfflineMode && (
              <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(syncProgress.current / syncProgress.total) * 100}%`,
                  }}
                />
              </div>
            )}
            {!isOfflineMode && !isSyncing && (
              <Button
                variant="outline"
                size="sm"
                className="w-full bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200"
                onClick={onSync}
              >
                Sincronizar ahora
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Versión Móvil (en la parte inferior) */}
      <div className="md:hidden fixed bottom-24 left-4 right-4 z-50 space-y-2">
        {isOfflineMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-lg p-3 flex items-center gap-2">
            <WifiOff className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                Modo sin conexión
              </p>
              <p className="text-xs text-amber-700">
                Las ventas se guardarán localmente
              </p>
            </div>
          </div>
        )}

        {pendingSalesCount > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw
                className={`h-5 w-5 text-blue-600 flex-shrink-0 ${isSyncing ? "animate-spin" : ""}`}
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">
                  {syncProgress.isSyncing && !isOfflineMode
                    ? `Sincronizando ${syncProgress.current}/${syncProgress.total}...`
                    : `${pendingSalesCount} operacione${pendingSalesCount !== 1 ? "s" : ""} pendiente${pendingSalesCount !== 1 ? "s" : ""}`}
                </p>
                <p className="text-xs text-blue-700">
                  {isOfflineMode
                    ? "Se sincronizarán cuando vuelva la conexión"
                    : isSyncing
                      ? "Procesando..."
                      : "Listas para sincronizar"}
                </p>
              </div>
            </div>
            {syncProgress.isSyncing && !isOfflineMode && (
              <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(syncProgress.current / syncProgress.total) * 100}%`,
                  }}
                />
              </div>
            )}
            {!isOfflineMode && !isSyncing && (
              <Button
                variant="outline"
                size="sm"
                className="w-full bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200"
                onClick={onSync}
              >
                Sincronizar ahora
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
